// components/pricing/FAQSection.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Link } from "@/i18n/navigation";

type FAQItem = { q: string; a: string };

export default function FAQSection() {
  const t = useTranslations("pricing.faq");
  const sectionRef = useRef<HTMLElement>(null);
  const items = t.raw("items") as FAQItem[];
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const cards = section.querySelectorAll<HTMLElement>("[data-faq]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const delay = el.dataset.delay ?? "0";
            el.style.transition = `opacity 0.5s ease ${delay}s, transform 0.5s ease ${delay}s`;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    cards.forEach((card) => {
      card.style.opacity = "0";
      card.style.transform = "translateY(16px)";
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" ref={sectionRef} className="relative bg-panel px-6 py-24 overflow-hidden">
      {/* Grid background */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgb(var(--accent) / 0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgb(var(--accent) / 0.12) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse 70% 80% at 50% 50%, black, transparent 90%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 80% at 50% 50%, black, transparent 90%)",
        }}
      />

      {/* Radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(ellipse 50% 40% at 50% 20%, rgb(var(--accent) / 0.08) 0%, transparent 70%)",
        }}
      />

      {/* Decorative hexagons */}
      <HexDecor className="absolute left-[5%] top-16 w-20 h-20 opacity-15" />
      <HexDecor className="absolute right-[7%] top-32 w-14 h-14 opacity-20" />
      <HexDecor className="absolute left-[8%] bottom-20 w-16 h-16 opacity-15" />
      <HexDecor className="absolute right-[5%] bottom-32 w-24 h-24 opacity-10" />

      <div className="relative z-10 max-w-3xl mx-auto">
        <div className="mb-12 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <HelpCircle className="w-6 h-6 text-accent" />
            <h2 className="text-3xl md:text-4xl font-bold text-fore">{t("title")}</h2>
          </div>
          <p className="text-sec">{t("subtitle")}</p>
        </div>

        <div className="flex flex-col gap-3">
          {items.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                data-faq
                data-delay={(i * 0.05).toFixed(2)}
                className={`
                  rounded-2xl border bg-card transition-all duration-300 overflow-hidden
                  ${isOpen ? "border-accent-md shadow-accent" : "border-base hover:border-accent-md"}
                `}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-start"
                >
                  <span className={`text-sm md:text-base font-semibold ${isOpen ? "text-accent" : "text-fore"}`}>
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? "rotate-180 text-accent" : "text-mute"
                    }`}
                  />
                </button>
                <div
                  className="grid transition-all duration-300 ease-in-out"
                  style={{
                    gridTemplateRows: isOpen ? "1fr" : "0fr",
                  }}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-sec text-sm md:text-[15px] leading-relaxed">
                      {renderWithLinks(item.a)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

    function renderWithLinks(text: string) {
  const parts = text.split(/(\/[a-zA-Z0-9/_-]+)/g);
  return parts.map((part, i) =>
    /^\/[a-zA-Z0-9/_-]+$/.test(part) ? (
      <Link
        key={i}
        href={part}
        className="text-accent underline hover:opacity-80"
      >
        {part}
      </Link>
    ) : (
      part
    )
  );
}
function HexDecor({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 115" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <polygon
        points="50,5 95,27.5 95,87.5 50,110 5,87.5 5,27.5"
        stroke="rgb(var(--accent))"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}